import { useEffect, useState } from 'react';

import MapCard from '@App/components/MapCard/MapCard';
import MapContainer from '@App/components/MapContainer/MapContainer';
import { useMaplibreMap } from '@App/components/MaplibreMap/maplibre-map.context';
import { useMapPopup, useLayerHover } from '@App/components/MaplibreMap/maplibre-map.hooks';
import { removeLayerAndSource } from '@App/components/MaplibreMap/maplibre-map.utils';
import { useObservations } from '@services/query.service';
import TranslationsProvider from '@utils/i18n/i18n';
import translator from '@App/app.translator';

import SpeciesPopup from './SpeciesPopup/SpeciesPopup';
import SpeciesList from './SpeciesList/SpeciesList';

const SOURCE_ID = 'catalan-beaches';
const FILL_LAYER_ID = 'catalan-beaches-fill';
const LABEL_LAYER_ID = 'catalan-beaches-labels';

function SpeciesLayer() {
  const { map, isLoading } = useMaplibreMap();
  const { data: observations } = useObservations();
  const { openPopup, closePopup } = useMapPopup();
  const [selectedFeature, setSelectedFeature] = useState(null);

  useLayerHover(map, FILL_LAYER_ID);

  useEffect(() => {
    if (!map || isLoading) return undefined;

    map.addSource(SOURCE_ID, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    });

    map.addLayer({
      id: FILL_LAYER_ID,
      type: 'fill',
      source: SOURCE_ID,
      paint: {
        'fill-color': '#1f78b4',
        'fill-opacity': 0.35,
        'fill-outline-color': '#155d8b',
      },
    }, 'z-index-2');

    map.addLayer({
      id: LABEL_LAYER_ID,
      type: 'symbol',
      source: SOURCE_ID,
      layout: {
        'text-field': [
          'to-string',
          [
            '+',
            ['coalesce', ['get', 'molluscs', ['get', 'species_groups', ['get', 'observationCounts']]], 0],
            ['coalesce', ['get', 'crustaceans', ['get', 'species_groups', ['get', 'observationCounts']]], 0],
            ['coalesce', ['get', 'fish', ['get', 'species_groups', ['get', 'observationCounts']]], 0],
          ],
        ],
        'text-size': 12,
        'text-anchor': 'center',
        'text-allow-overlap': false,
      },
      paint: {
        'text-color': '#155d8b',
        'text-halo-color': '#FFFFFF',
        'text-halo-width': 2,
      },
    }, 'z-index-5');

    return () => {
      closePopup();
      removeLayerAndSource(map, LABEL_LAYER_ID);
      removeLayerAndSource(map, FILL_LAYER_ID, SOURCE_ID);
    };
  }, [isLoading, map, openPopup, closePopup]);

  useEffect(() => {
    if (!map) return undefined;

    const handleClick = (e) => {
      if (!e.features?.[0]) return;

      const feature = observations?.find(f => String(f.properties.id) === String(e.features[0].properties.id));

      if (!feature) return;

      openPopup(
        map,
        e.lngLat,
        <TranslationsProvider translator={translator}>
          <SpeciesPopup feature={feature} />
        </TranslationsProvider>,
        () => setSelectedFeature(null),
      );

      setSelectedFeature(feature);
    };

    map.on('click', FILL_LAYER_ID, handleClick);

    return () => {
      map?.off('click', FILL_LAYER_ID, handleClick);
    };
  }, [map, observations, openPopup]);

  useEffect(() => {
    if (isLoading || !observations || !map?.getSource(SOURCE_ID)) return;

    map.getSource(SOURCE_ID).setData({
      type: 'FeatureCollection',
      features: observations,
    });
  }, [isLoading, observations, map]);

  return (
    <>
      {selectedFeature && (
        <MapContainer position="bottom-right">
          <MapCard className="species-layer__species-list">
            <SpeciesList feature={selectedFeature} />
          </MapCard>
        </MapContainer>
      )}
    </>
  );
}

export default SpeciesLayer;
